/**
 * Stable Strike Parameter Coercion
 *
 * Root cause fix: Stable bar stored SPY custom strikes (mmec_strike_min/max 400–550).
 * Index options (SPX/SPXW/NDX/RUT/etc.) need ATM strike_range (e.g. 20), not that band.
 *
 * When the selected underlying is a high-strike index AND localStorage has a custom
 * min/max band that looks like an ETF-scale range (<2000), we fall back to the ATM
 * count-based strike_range. This prevents "Loaded 0 rows" on index symbols.
 */

// ── high-strike index roots ─────────────────────────────────────────────────────

/** Roots whose listed strikes are typically >> $1k (index options). */
export const HIGH_STRIKE_INDEX_ROOTS: ReadonlySet<string> = new Set([
    "SPX",
    "SPXW",
    "SPXQ",
    "SPXPM",
    "NDX",
    "NDXP",
    "RUT",
    "RUTW",
    "RUTQ",
    "VIX",
    "VIXW",
    "DJX",
    "XSP",
    "XSPA",
    "XSPPM",
    "XSPAM",
    "MXEA",
]);

// ── helpers ──────────────────────────────────────────────────────────────────────

export function normalizeStableUnderlyingRoot(u: string): string {
    return String(u || "")
        .trim()
        .toUpperCase()
        .replace(/^\$/, "")
        .replace(/\.X$/i, "")
        .replace(/\..+$/, "");
}

export function isHighStrikeIndexRoot(symbol: string): boolean {
    return HIGH_STRIKE_INDEX_ROOTS.has(normalizeStableUnderlyingRoot(symbol));
}

export function stableFetchUsesSpyScaleCustomBand(params: {
    strike_min?: string;
    strike_max?: string;
}): boolean {
    const maxN = parseFloat(String(params.strike_max ?? ""));
    return Number.isFinite(maxN) && maxN < 2000;
}

// ── localStorage prefs ───────────────────────────────────────────────────────────

export interface StrikeParams {
    strike_range?: string;
    strike_min?: string;
    strike_max?: string;
}

function getRawStrikePrefs(): StrikeParams {
    if (typeof localStorage === "undefined") return {};
    try {
        const mode = (
            localStorage.getItem("mmec_strike_mode") || "count"
        ).toLowerCase();
        if (mode === "all") return { strike_range: "all" };
        if (mode === "custom") {
            const min = (localStorage.getItem("mmec_strike_min") || "").trim();
            const max = (localStorage.getItem("mmec_strike_max") || "").trim();
            const minN = parseFloat(min);
            const maxN = parseFloat(max);
            if (Number.isFinite(minN) && Number.isFinite(maxN)) {
                return { strike_min: min, strike_max: max };
            }
        }
        const range = (
            localStorage.getItem("mmec_strike_range") || "20"
        ).trim();
        return range ? { strike_range: range } : {};
    } catch {
        return {};
    }
}

/**
 * Return strike params suitable for the given underlyings.
 * If any selected underlying is a high-strike index AND the stored custom band
 * looks like an ETF-scale range (<2000 max), fall back to ATM count.
 */
export function coerceStrikeParamsForUnderlyings(
    selectedUnderlyings: string[],
): StrikeParams {
    const base = getRawStrikePrefs();
    if (base.strike_min == null || base.strike_max == null) return base;

    const indexSelected = selectedUnderlyings.some((s) =>
        isHighStrikeIndexRoot(s),
    );
    if (indexSelected && stableFetchUsesSpyScaleCustomBand(base)) {
        const strikeRange = (
            localStorage.getItem("mmec_strike_range") || "20"
        ).trim();
        return strikeRange
            ? { strike_range: strikeRange }
            : { strike_range: "20" };
    }
    return base;
}

/**
 * Persist appropriate strike mode for the given symbol.
 *
 * When the user selects a high-strike index and localStorage contains a custom
 * band clearly meant for ETFs (e.g. 400–550), auto-switch to count mode so the
 * stored prefs don't conflict with the coercion path.
 *
 * Returns true if localStorage was modified (caller should update React state).
 */
export function persistStrikePrefsForSymbol(symbol: string): boolean {
    if (typeof localStorage === "undefined") return false;
    const root = normalizeStableUnderlyingRoot(symbol);
    if (!isHighStrikeIndexRoot(root)) return false;

    try {
        const mode = (
            localStorage.getItem("mmec_strike_mode") || "count"
        ).toLowerCase();
        if (mode !== "custom") return false;

        const min = parseFloat(
            (localStorage.getItem("mmec_strike_min") || "").trim(),
        );
        const max = parseFloat(
            (localStorage.getItem("mmec_strike_max") || "").trim(),
        );
        if (!Number.isFinite(max) || max >= 2000) return false;
        if (!Number.isFinite(min)) return false;

        // Custom band is SPY-scale (<2000); the index would get 0 rows.
        // Switch to count mode and retain the stored strike_range.
        localStorage.setItem("mmec_strike_mode", "count");
        localStorage.removeItem("mmec_strike_min");
        localStorage.removeItem("mmec_strike_max");
        console.log(
            `[stableStrikeParams] Auto-coerced ${symbol} from custom ${min}–${max} to count`,
        );
        return true;
    } catch {
        return false;
    }
}

/**
 * Extra query segment for stable `accumulated-data` URLs (strike/expiry from localStorage prefs).
 * Append as `&${getStableAccumulatedDataExtraQueryParams()}` after underlyings; returns "" if nothing to add.
 */
export function getStableAccumulatedDataExtraQueryParams(
    selectedUnderlyingsOverride?: string[],
): string {
    if (typeof localStorage === "undefined") return "";
    try {
        const underlyings =
            selectedUnderlyingsOverride ??
            (() => {
                // Import lazily to avoid circular dependency; the caller provides
                // the list, so we just use the override.
                return [];
            })();
        const strikeParams = coerceStrikeParamsForUnderlyings(underlyings);
        const expiryParams = getExpiryParamsRaw();
        const parts: string[] = [];
        if (expiryParams.expiry_range) {
            parts.push(
                `expiry_range=${encodeURIComponent(expiryParams.expiry_range)}`,
            );
        }
        if (expiryParams.expiry) {
            parts.push(`expiry=${encodeURIComponent(expiryParams.expiry)}`);
        }
        if (
            strikeParams.strike_min != null &&
            strikeParams.strike_max != null
        ) {
            parts.push(
                `strike_min=${encodeURIComponent(String(strikeParams.strike_min))}`,
            );
            parts.push(
                `strike_max=${encodeURIComponent(String(strikeParams.strike_max))}`,
            );
        } else if (strikeParams.strike_range) {
            parts.push(
                `strike_range=${encodeURIComponent(strikeParams.strike_range)}`,
            );
        }
        return parts.join("&");
    } catch {
        return "";
    }
}

function getExpiryParamsRaw(): { expiry_range?: string; expiry?: string } {
    if (typeof localStorage === "undefined") return {};
    try {
        const expiryRange = (
            localStorage.getItem("mmec_expiry_range") || "weekly"
        ).trim();
        const exactDate = (
            localStorage.getItem("mmec_expiry_date") || ""
        ).trim();
        if (expiryRange === "specific-date" && exactDate) {
            return { expiry_range: "specific-date", expiry: exactDate };
        }
        return expiryRange ? { expiry_range: expiryRange } : {};
    } catch {
        return {};
    }
}
